// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Card from 'antd/lib/card';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import { CloseOutlined } from '@ant-design/icons';
import { getCore } from 'cvat-core-wrapper';

import { CombinedState } from 'reducers';
import { removeTaskFromRunAsync, updateJobQAStateAsync } from 'actions/labeling-runs-actions';

const QA_STATE_COLORS: Record<string, string> = {
    done: '#52c41a',
    labeling: '#1890ff',
    qa: '#13c2c2',
    qa_complete: '#13c2c2',
    sent_back: '#cf1322',
    new: '#d9d9d9',
    skip: '#8c8c8c',
};

const core = getCore();

interface Props {
    task: any;
    runId: number;
    qaStates: Record<number, string>;
}

export default function RunTaskItemComponent({ task, runId, qaStates }: Props): JSX.Element {
    const dispatch = useDispatch();
    const history = useHistory();
    const [jobs, setJobs] = useState<any[]>([]);
    const currentUser = useSelector((state: CombinedState) => state.auth.user);
    const canManage = !!currentUser && (
        currentUser.isSuperuser || currentUser.isStaff || currentUser.groups.includes('admin')
    );
    const jobIds: number[] = task.job_ids || [];

    useEffect(() => {
        // Fetch full job instances for auto-sync only (not needed for display)
        core.tasks.get({ id: task.task_id }).then(([t]: any[]) => {
            if (t) setJobs(t.jobs);
        });
    }, [task.task_id]);

    useEffect(() => {
        if (!jobs.length) return;
        jobs.forEach((job: any) => {
            const currentQA = qaStates[job.id] || 'new';
            if (job.stage === 'annotation' && job.state === 'in progress' && currentQA === 'new') {
                dispatch(updateJobQAStateAsync(runId, job.id, 'labeling') as any);
            } else if (job.stage === 'acceptance' && job.state === 'completed' &&
                       (currentQA === 'new' || currentQA === 'labeling')) {
                dispatch(updateJobQAStateAsync(runId, job.id, 'done') as any);
            }
        });
    }, [jobs]);

    const totalJobs = task.job_count || jobIds.length;
    const stateCounts = Object.fromEntries(
        Object.keys(QA_STATE_COLORS).map((s) => [s, jobIds.filter((id: number) => (qaStates[id] || 'new') === s).length]),
    );
    const doneJobs = stateCounts.done;
    const skipJobs = stateCounts.skip;
    const relevantTotal = totalJobs - skipJobs;

    return (
        <Card
            size='small'
            style={{ marginBottom: 12 }}
            className='cvat-run-task-item'
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                    <Text
                        strong
                        style={{ cursor: 'pointer', color: '#1890ff', fontSize: 18 }}
                        onClick={() => history.push(`/tasks/${task.task_id}`)}
                    >
                        {`Task #${task.task_id}`}
                    </Text>
                    <div style={{ marginTop: 8 }}>
                        {totalJobs > 0 ? (
                            <div style={{
                                display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#f0f0f0',
                            }}
                            >
                                {Object.entries(QA_STATE_COLORS).map(([state, color]) => {
                                    const count = stateCounts[state] || 0;
                                    if (!count) return null;
                                    const width = `${(count / totalJobs) * 100}%`;
                                    return (
                                        <Tooltip key={state} title={`${state}: ${count}`}>
                                            <div style={{ width, background: color }} />
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{
                                height: 8, borderRadius: 4, background: '#f0f0f0',
                            }}
                            />
                        )}
                        <Text type='secondary' style={{ fontSize: 16, marginTop: 4, display: 'block' }}>
                            {`${doneJobs} done / ${relevantTotal} total`}
                            {(stateCounts.labeling + stateCounts.qa + stateCounts.qa_complete + stateCounts.sent_back) > 0 && ` · ${stateCounts.labeling + stateCounts.qa + stateCounts.qa_complete + stateCounts.sent_back} in progress`}
                            {skipJobs > 0 && ` · ${skipJobs} skipped`}
                        </Text>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
                    <Button
                        type='primary'
                        size='large'
                        onClick={() => history.push(`/labeling-runs/${runId}/tasks/${task.task_id}`)}
                    >
                        Open
                    </Button>
                    {canManage && (
                        <Button
                            type='text'
                            size='large'
                            icon={<CloseOutlined />}
                            onClick={() => dispatch(removeTaskFromRunAsync(runId, task.task_id) as any)}
                            title='Remove task from run'
                        />
                    )}
                </div>
            </div>
        </Card>
    );
}
